import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const { data } = await api.get('/auth/me');
          setUser(data.user);
        } catch {
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    return data;
  }, []);

  const verifyOtp = useCallback(async (otpToken, code) => {
    const { data } = await api.post('/auth/verify-otp', { otpToken, code });
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  }, []);

  const resendOtp = useCallback(async (otpToken) => {
    const { data } = await api.post('/auth/resend-otp', { otpToken });
    return data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const { data } = await api.post('/auth/forgot-password', { email });
    return data;
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    const { data } = await api.post('/auth/reset-password', { token, password });
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    login,
    verifyOtp,
    resendOtp,
    forgotPassword,
    resetPassword,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    // Permission helpers. Admins always pass; a section/price is allowed unless
    // the user's permissions explicitly set it to false (unconfigured = full).
    canAccessSection: (section) => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      const p = user.permissions;
      if (!p || !p.sections) return true;
      return p.sections[section] !== false;
    },
    canViewPrice: () => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      return user.permissions?.view_price !== false;
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
